import { atom } from "jotai"
import { focusAtom } from "jotai-optics"
import { store } from "@/store/store"
import { saveCurrentAppState } from "@/store/store-history"
import { stateAtom } from "@/store/undoable-state"
import {
	cameraFiltersAtom,
	colorFiltersAtom,
	displayGroupAtom,
	fileFormatFiltersAtom,
	flagFiltersAtom,
	isCalibrationAtom,
	pathFiltersAtom,
	projectAtom,
	projectPathsAtom,
	ratingFiltersAtom,
	updateCurrentProject,
	visibilityFiltersAtom,
} from "../project/projects-state"
import {
	currentPhotoAtom,
	displayedPhotosAtom,
	searchInputAtom,
	searchResultPhotoIdsAtom,
	selectedPhotosAtom,
} from "./photos-state"
import { lastSelectionFromClickAtom } from "./photos-selection-state"
import {
	activeEditingToolPanelAtom,
	showingCropPanelAtom,
	showingHealingPanelAtom,
	showingMaskingPanelAtom,
	showingRatingBarAtom,
} from "../ui-views-state"
import { DisplayGroup, SortOption, Visibility } from "@/lib/catalog/types"
import { isCurrentFileLoaded, loadCurrentFile } from "@/store/editor/photo-loading"
import { atomWithStorage } from "jotai/utils"
import { requestTransition } from "@/utils/useViewTransition"

export const photosViewAtom = focusAtom(stateAtom, (optic) => optic.prop("photosView"))

export const autoSelectFirstPhotoIfNeeded = () => {
	const currentPhoto = store.get(currentPhotoAtom)
	if (!currentPhoto) {
		const firstPhoto = store.get(displayedPhotosAtom)[0]

		if (firstPhoto) {
			store.set(currentPhotoAtom, firstPhoto)
			store.set(selectedPhotosAtom, [firstPhoto])
		}
		return
	}

	const selectedPhotos = store.get(selectedPhotosAtom)
	if (!selectedPhotos.some((photo) => photo.id === currentPhoto.id)) {
		store.set(selectedPhotosAtom, [currentPhoto])
	}
}

export const openGridView = () => {
	if (store.get(isCalibrationAtom)) {
		return
	}

	requestTransition("grid")
	store.set(showingRatingBarAtom, false)

	autoSelectFirstPhotoIfNeeded()

	saveCurrentAppState(`Open grid view`)
}

export const openFocusView = () => {
	autoSelectFirstPhotoIfNeeded()

	const previous = store.get(photosViewAtom)

	if (previous === "grid") {
		// Reset click tracking when switching from grid to focus view
		// This allows the filmstrip to auto-scroll to the selected photo
		store.set(lastSelectionFromClickAtom, false)
	}

	if (!isCurrentFileLoaded()) {
		void loadCurrentFile({
			kinds: ["preview", "full"],
			onPreviewReady: () => store.set(photosViewAtom, "focus"),
		})
	} else {
		store.set(photosViewAtom, "focus")
	}

	store.set(showingRatingBarAtom, true)

	saveCurrentAppState(`Open focus view`)
}

export const openCompareView = () => {
	if (!store.get(currentPhotoAtom)) {
		return
	}
	const previous = store.get(photosViewAtom)
	store.set(photosViewAtom, "before/after")

	if (previous === "grid") {
		void loadCurrentFile()
	}
	saveCurrentAppState(`Open compare view`)

	//Cannot show crop when in compare view
	if (store.get(showingCropPanelAtom)) {
		store.set(showingCropPanelAtom, false)
	}

	store.set(showingRatingBarAtom, true)
}

export const toggleCropView = () => {
	if (!store.get(currentPhotoAtom)) {
		return
	}

	if (store.get(photosViewAtom) !== "focus") {
		openFocusView()
		store.set(showingCropPanelAtom, true)
	} else if (!store.get(showingCropPanelAtom)) {
		store.set(showingCropPanelAtom, true)
	} else {
		store.set(showingCropPanelAtom, false)
	}
}

export const toggleHealingView = () => {
	if (!store.get(currentPhotoAtom)) {
		return
	}

	if (store.get(photosViewAtom) === "grid") {
		openFocusView()
		store.set(showingHealingPanelAtom, true)
	} else if (!store.get(showingHealingPanelAtom)) {
		store.set(showingHealingPanelAtom, true)
	} else {
		store.set(showingHealingPanelAtom, false)
	}
}

export const toggleMaskView = () => {
	if (!store.get(currentPhotoAtom)) {
		return
	}

	if (store.get(photosViewAtom) === "grid") {
		openFocusView()
		store.set(showingMaskingPanelAtom, true)
	} else if (!store.get(showingMaskingPanelAtom)) {
		store.set(showingMaskingPanelAtom, true)
	} else {
		store.set(showingMaskingPanelAtom, false)
	}
}

export const openBasicEditView = () => {
	if (!store.get(currentPhotoAtom)) {
		return
	}

	if (store.get(photosViewAtom) === "grid") {
		openFocusView()
	}

	if (store.get(activeEditingToolPanelAtom) !== "edit") {
		store.set(activeEditingToolPanelAtom, "edit")
	}
}

export const FilterTypes = [
	"color",
	"rating",
	"flag",
	"camera model",
	"folder",
	"file type",
	"visibility",
] as const
export type FilterType = (typeof FilterTypes)[number]

export const displayingFiltersAtom = atomWithStorage<Set<FilterType>>(
	"project_displaying_filters",
	new Set<FilterType>(FilterTypes),
	{
		getItem: (key, initialValue) => {
			try {
				const stored = localStorage.getItem(key)
				if (stored) {
					const parsed: FilterType[] = JSON.parse(stored)
					return new Set<FilterType>(parsed)
				}
			} catch {
				// ignore
			}
			return initialValue
		},
		setItem: (key, value) => {
			try {
				localStorage.setItem(key, JSON.stringify(Array.from(value)))
			} catch {
				// ignore
			}
		},
		removeItem: (key) => {
			try {
				localStorage.removeItem(key)
			} catch {
				// ignore
			}
		},
	},
)

export const resetAllFilters = () => {
	const updatedFilters = {
		pathFilters: store.get(projectPathsAtom).map((p) => p.path),
		colorFilters: [],
		ratingFilters: [],
		flagFilters: [],
		cameraFilters: [],
		fileFormatFilters: [],
		visibilityFilters: ["visible"] as Visibility[],
		displayGroup: "all" as DisplayGroup,
	}

	// Clear search input and results. Since search is not a part of permanent project state.
	store.set(searchInputAtom, "")
	store.set(searchResultPhotoIdsAtom, new Set())

	void updateCurrentProject(updatedFilters)
}

const sortedSetValues = (values: Set<unknown>) => Array.from(values).map(String).sort()

export const activeFiltersKeyAtom = atom((get) => {
	return JSON.stringify({
		pathFilters: sortedSetValues(get(pathFiltersAtom)),
		colorFilters: sortedSetValues(get(colorFiltersAtom)),
		ratingFilters: sortedSetValues(get(ratingFiltersAtom)),
		cameraFilters: sortedSetValues(get(cameraFiltersAtom)),
		fileFormatFilters: sortedSetValues(get(fileFormatFiltersAtom)),
		visibilityFilters: sortedSetValues(get(visibilityFiltersAtom)),
		displayGroup: get(displayGroupAtom),
	})
})

export const hasFiltersAtom = atom((get) => {
	return (
		!get(pathFiltersAtom).has(get(projectAtom)?.name ?? "") ||
		get(colorFiltersAtom).size > 0 ||
		get(ratingFiltersAtom).size > 0 ||
		get(flagFiltersAtom).size > 0 ||
		get(visibilityFiltersAtom).size === 0 ||
		get(cameraFiltersAtom).size > 0 ||
		get(fileFormatFiltersAtom).size > 0 ||
		get(displayGroupAtom) != "all" ||
		get(searchInputAtom).trim().length > 0
	)
})

export const gridSizeAtom = focusAtom(stateAtom, (optic) => optic.prop("gridSize"))

export const sortOptionAtom = atom(
	(get) => {
		const project = get(projectAtom)
		return project?.sortOption ?? "filenameAscending"
	},
	(get, _, sortOption: SortOption) => {
		const project = get(projectAtom)
		if (project) {
			void updateCurrentProject({ sortOption })
		}
	},
)

export const gridColAtom = atom((get) => {
	const size = get(gridSizeAtom)

	switch (size) {
		case 1:
			return 24
		case 2:
			return 18
		case 3:
			return 12
		case 4:
			return 8
		case 5:
			return 6
		case 6:
			return 5
		case 7:
			return 4
		case 8:
			return 3
		case 9:
			return 2
	}
})
export { displayGroupAtom }
